"use client";

import React, { useEffect, useState } from "react";

const DEFAULT_PHONE = "573009876543";
const DEFAULT_MESSAGE = "Hola equipo SYNAPTIX, necesito ayuda o información sobre los servicios de ecografía veterinaria.";

export function WhatsAppIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
    </svg>
  );
}

export function useWhatsAppContact(customPhone?: string, customMsg?: string) {
  const [phone, setPhone] = useState(customPhone || DEFAULT_PHONE);

  useEffect(() => {
    if (customPhone) return;
    fetch("/api/admin/settings/logistics-contact")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.phone) {
          const clean = data.phone.replace(/[^0-9]/g, "");
          if (clean) setPhone(clean);
        }
      })
      .catch(() => {});
  }, [customPhone]);

  const getUrl = (msg?: string) => {
    const text = encodeURIComponent(msg || customMsg || DEFAULT_MESSAGE);
    return `https://wa.me/${phone}?text=${text}`;
  };

  return { phone, getUrl };
}

interface WhatsAppFloatProps {
  phone?: string;
  message?: string;
  label?: string;
}

export function WhatsAppFloat({ phone: customPhone, message, label = "¿Necesitas ayuda?" }: WhatsAppFloatProps) {
  const { getUrl } = useWhatsAppContact(customPhone, message);

  return (
    <a
      href={getUrl(message)}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold px-4 py-3 rounded-full shadow-[0_0_25px_rgba(37,211,102,0.5)] hover:shadow-[0_0_35px_rgba(37,211,102,0.8)] transition-all duration-300 hover:scale-105 group"
      title="Escríbenos por WhatsApp"
    >
      <span className="relative flex h-3 w-3">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
        <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
      </span>
      <WhatsAppIcon className="w-6 h-6 fill-white" />
      <span className="text-sm font-extrabold tracking-wide hidden sm:inline-block pr-1">{label}</span>
    </a>
  );
}

interface WhatsAppButtonProps {
  phone?: string;
  message?: string;
  label?: string;
  variant?: "emerald" | "cyan" | "outline";
  className?: string;
}

export function WhatsAppInlineButton({
  phone: customPhone,
  message,
  label = "¿Necesitas ayuda? Escríbenos por WhatsApp",
  variant = "emerald",
  className = "",
}: WhatsAppButtonProps) {
  const { getUrl } = useWhatsAppContact(customPhone, message);

  let variantStyle = "bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-[0_0_20px_rgba(37,211,102,0.3)]";
  if (variant === "cyan") {
    variantStyle = "bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.2)]";
  } else if (variant === "outline") {
    variantStyle = "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.2)]";
  }

  return (
    <a
      href={getUrl(message)}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2.5 px-5 py-3 rounded-2xl font-bold text-sm transition-all duration-300 hover:scale-[1.02] ${variantStyle} ${className}`}
    >
      <WhatsAppIcon className="w-5 h-5 shrink-0 fill-current" />
      <span>{label}</span>
    </a>
  );
}
